import { RadioProps, Stack, Text, useRadioGroup } from '@chakra-ui/react';
import { stringToBoolean } from '@pelicargo/utils';
import { ComponentProps } from 'react';
import { useFormContext } from 'react-hook-form';

import { themeVar } from '../config/theme/tenantTheme';
import { SimpleFieldVariant } from '../utils/types';
import { Radio } from './Radio';
import { SimpleField } from './SimpleField';

type Option = {
  label: string;
  value: string | boolean;
};

type SimpleSwitchProps = RadioProps & {
  name: string;
  label?: string;
  direction?: 'row' | 'column';
  options: Option[];
  variant?: 'inline' | 'outline';
  fieldVariant?: SimpleFieldVariant;
  size?: 'sm' | 'md';
  simpleFieldProps?: Omit<ComponentProps<typeof SimpleField>, 'name' | 'label' | 'variant'>;
};

export const SimpleSwitch = ({
  name,
  label,
  options,
  fieldVariant,
  variant,
  direction = 'row',
  size = 'md',
  simpleFieldProps,
  ...rest
}: SimpleSwitchProps) => {
  const { setValue, watch } = useFormContext();
  const value = watch(name);

  const handleChange = (value: string) => {
    const parsedValue = stringToBoolean(value) ?? value;
    // Revalidate on pick so a "please answer this" error clears as soon as the
    // user answers, rather than lingering until the next submit.
    setValue(name, parsedValue, { shouldValidate: true });
  };

  const { getRootProps, getRadioProps } = useRadioGroup({
    name,
    value,
    onChange: handleChange,
  });

  const isCompact = size === 'sm';

  const renderRadio = (opt: Option) => {
    const radioProps = getRadioProps({ value: opt.value });
    return (
      <Radio
        key={String(opt.value)}
        variant={variant}
        size={size}
        w={isCompact ? 'auto' : 'full'}
        isVertical={direction === 'column'}
        tabIndex={0}
        _focus={{
          outline: 'none',
          boxShadow: '0 0 0 3px rgba(66, 153, 225, 0.6)',
          borderColor: 'secondary.500',
        }}
        {...radioProps}
        onKeyDown={(e) => {
          if (e.key === 'Enter') handleChange(String(opt.value));
        }}
      >
        <Text fontSize={isCompact ? 'sm' : 'md'} fontWeight="semibold" whiteSpace="nowrap">
          {opt.label}
        </Text>
      </Radio>
    );
  };

  return (
    <SimpleField name={name} label={label} variant={fieldVariant} {...simpleFieldProps}>
      <Stack
        direction={direction}
        h="full"
        w={isCompact ? 'fit-content' : 'full'}
        // The track behind the pills. `switch.bg` rather than leaving it on `bg.surface`, which is
        // shared with every card and field in the app - moving the switch should not move those.
        bg={themeVar('switch.bg', 'var(--chakra-colors-bg-surface)')}
        borderRadius={themeVar('switch.borderRadius', 'var(--chakra-radii-md)')}
        {...rest}
        {...getRootProps()}
      >
        {options.map(renderRadio)}
      </Stack>
    </SimpleField>
  );
};
